import type { Player, ResourceCost, SkillId } from './types';

/**
 * Skill-Web — radial talent tree replacing the v1 linear research nodes.
 *
 *   ┌─────────────────────────────────────┐
 *   │  CAPSTONE RING  (combat skills)     │
 *   │     execute · momentum · …          │
 *   ├─────────────────────────────────────┤
 *   │  SYNERGY RING  (synergy bonuses)    │
 *   │     data_vampirism · compound_processor · …│
 *   ├─────────────────────────────────────┤
 *   │  CORE RING  (universal stat ramps)  │
 *   │     vitality · alloy · aggression   │
 *   └─────────────────────────────────────┘
 *
 * Progression: Spend Clean Code (+ occasionally Nano-Chips) to level Core nodes.
 * Each Synergy unlocks once its prereq Core is ≥ Lv 1; each Capstone unlocks
 * once its prereq Synergy is ≥ Lv 3 AND the player meets the original
 * vault-tier gate (so endgame skills still need vault progression).
 */
export type SkillWebRing = 'core' | 'synergy' | 'capstone';

export type SkillWebEffect =
  // Core (Lv 1-5, applied multiplicatively in calcPlayerStats)
  | 'hp_pct'                   // +5 % MaxHP per level
  | 'def_pct'                  // +5 % DEF per level
  | 'atk_pct'                  // +5 % ATK per level
  // Synergy (Lv 1-5, custom hooks throughout combat / economy)
  | 'cd_to_hp'                 // 1 HP per CD collected this run
  | 'extra_boss_roll'          // +1 boss-loot roll per Lv (caps at 3)
  | 'kinetic_stack'            // +1 DEF stack per hit received (max 50)
  | 'salvage_yield'            // 60→100 % salvage refund
  | 'heat_max_boost'           // +25 max heat + +5 % siphon dmg per Lv
  | 'cd_buffer_to_atk'         // +1 % ATK per 10 k buffered CD (max +50 %)
  // Capstone — gates a SkillId (binary unlock, level interp via skills.ts)
  | 'capstone_unlock';

export interface SkillWebNode {
  id: string;
  ring: SkillWebRing;
  /** Display angle in degrees (0 = right, 90 = top). */
  angleDeg: number;
  /** Other node IDs that must be ≥ minLevel for this one to unlock. */
  prerequisites: Array<{ nodeId: string; minLevel: number }>;
  /** Player vault-tier requirement (capstones inherit the original tier-gate). */
  unlockVaultTier?: number;
  maxLevel: number;
  effect: SkillWebEffect;
  /** For capstone nodes — links to the SkillId that becomes available. */
  capstoneSkillId?: SkillId;
  /** Display label key for i18n. */
  nameKey: string;
  /** Cost calculator: returns the cost to advance from `currentLevel` to `currentLevel + 1`. */
  cost: (currentLevel: number) => ResourceCost;
}

const coreCost = (lv: number): ResourceCost => ({
  cleanCode: Math.ceil(500 * Math.pow(2, lv)),
  nano_chip: Math.max(1, lv),
});

const synergyCost = (lv: number): ResourceCost => ({
  cleanCode: Math.ceil(2000 * Math.pow(2, lv)),
  nano_chip: 2 * (lv + 1),
});

const capstoneCost = (lv: number): ResourceCost => lv >= 1
  ? { cleanCode: 0 }              // already unlocked
  : { cleanCode: 5000, nano_chip: 5, data_track: 2 };

/** Master node table. Order doesn't matter for logic — only for default UI traversal. */
export const SKILL_WEB_NODES: SkillWebNode[] = [
  // ── Core Ring (3 nodes, equilateral triangle) ─────────────────────
  {
    id: 'vitality', ring: 'core', angleDeg: 90,
    prerequisites: [], maxLevel: 5,
    effect: 'hp_pct', nameKey: 'vitality',
    cost: coreCost,
  },
  {
    id: 'alloy', ring: 'core', angleDeg: 210,
    prerequisites: [], maxLevel: 5,
    effect: 'def_pct', nameKey: 'alloy',
    cost: coreCost,
  },
  {
    id: 'aggression', ring: 'core', angleDeg: 330,
    prerequisites: [], maxLevel: 5,
    effect: 'atk_pct', nameKey: 'aggression',
    cost: coreCost,
  },

  // ── Synergy Ring (6 nodes, each branches off one Core) ────────────
  {
    id: 'data_vampirism', ring: 'synergy', angleDeg: 60,
    prerequisites: [{ nodeId: 'vitality', minLevel: 1 }], maxLevel: 5,
    effect: 'cd_to_hp', nameKey: 'data_vampirism',
    cost: synergyCost,
  },
  {
    id: 'extra_boss_roll', ring: 'synergy', angleDeg: 120,
    prerequisites: [{ nodeId: 'vitality', minLevel: 1 }], maxLevel: 3,
    effect: 'extra_boss_roll', nameKey: 'extra_boss_roll',
    cost: synergyCost,
  },
  {
    id: 'kinetic_stack', ring: 'synergy', angleDeg: 180,
    prerequisites: [{ nodeId: 'alloy', minLevel: 1 }], maxLevel: 5,
    effect: 'kinetic_stack', nameKey: 'kinetic_stack',
    cost: synergyCost,
  },
  {
    id: 'salvage_yield', ring: 'synergy', angleDeg: 240,
    prerequisites: [{ nodeId: 'alloy', minLevel: 1 }], maxLevel: 5,
    effect: 'salvage_yield', nameKey: 'salvage_yield',
    cost: synergyCost,
  },
  {
    id: 'thermodynamics', ring: 'synergy', angleDeg: 300,
    prerequisites: [{ nodeId: 'aggression', minLevel: 1 }], maxLevel: 5,
    effect: 'heat_max_boost', nameKey: 'thermodynamics',
    cost: synergyCost,
  },
  {
    id: 'compound_processor', ring: 'synergy', angleDeg: 0,
    prerequisites: [{ nodeId: 'aggression', minLevel: 1 }], maxLevel: 5,
    effect: 'cd_buffer_to_atk', nameKey: 'compound_processor',
    cost: synergyCost,
  },

  // ── Capstone Ring (combat skills, binary unlock) ─────────────────
  {
    id: 'cap_execute', ring: 'capstone', angleDeg: 60, unlockVaultTier: 1,
    prerequisites: [{ nodeId: 'data_vampirism', minLevel: 3 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'execute', nameKey: 'cap_execute',
    cost: capstoneCost,
  },
  {
    id: 'cap_momentum', ring: 'capstone', angleDeg: 120, unlockVaultTier: 1,
    prerequisites: [{ nodeId: 'extra_boss_roll', minLevel: 1 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'momentum', nameKey: 'cap_momentum',
    cost: capstoneCost,
  },
  {
    id: 'cap_shield_bash', ring: 'capstone', angleDeg: 180, unlockVaultTier: 2,
    prerequisites: [{ nodeId: 'kinetic_stack', minLevel: 3 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'shield_bash', nameKey: 'cap_shield_bash',
    cost: capstoneCost,
  },
  {
    id: 'cap_market_manip', ring: 'capstone', angleDeg: 240, unlockVaultTier: 3,
    prerequisites: [{ nodeId: 'salvage_yield', minLevel: 3 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'market_manipulation', nameKey: 'cap_market_manip',
    cost: capstoneCost,
  },
  {
    id: 'cap_fireball', ring: 'capstone', angleDeg: 300, unlockVaultTier: 1,
    prerequisites: [{ nodeId: 'thermodynamics', minLevel: 3 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'fireball', nameKey: 'cap_fireball',
    cost: capstoneCost,
  },
  {
    id: 'cap_lightning_bolt', ring: 'capstone', angleDeg: 330, unlockVaultTier: 3,
    prerequisites: [{ nodeId: 'thermodynamics', minLevel: 5 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'lightning_bolt', nameKey: 'cap_lightning_bolt',
    cost: capstoneCost,
  },
  {
    id: 'cap_frost_lance', ring: 'capstone', angleDeg: 0, unlockVaultTier: 5,
    prerequisites: [{ nodeId: 'compound_processor', minLevel: 3 }], maxLevel: 1,
    effect: 'capstone_unlock', capstoneSkillId: 'frost_lance', nameKey: 'cap_frost_lance',
    cost: capstoneCost,
  },
];

export function getSkillWebNode(id: string): SkillWebNode | undefined {
  return SKILL_WEB_NODES.find((n) => n.id === id);
}

/** Current owned level of a skill-web node (0 = locked / not bought yet). */
export function nodeLevel(player: Player, id: string): number {
  return player.skillWebNodes?.[id] ?? 0;
}

/** True if all prerequisites are met for the given node. */
export function isNodeAvailable(player: Player, node: SkillWebNode): boolean {
  // Capstones additionally need vault-tier progression
  if (node.unlockVaultTier && (player.highestVaultTier ?? 0) < node.unlockVaultTier) {
    return false;
  }
  for (const req of node.prerequisites) {
    if (nodeLevel(player, req.nodeId) < req.minLevel) return false;
  }
  return true;
}

/** Returns the SkillId that the given capstone node unlocks (or undefined). */
export function capstoneSkillFor(node: SkillWebNode): SkillId | undefined {
  return node.capstoneSkillId;
}

/** All capstone SkillIds the player has unlocked via the web. */
export function unlockedCapstones(player: Player): SkillId[] {
  const out: SkillId[] = [];
  for (const node of SKILL_WEB_NODES) {
    if (node.ring !== 'capstone' || !node.capstoneSkillId) continue;
    if (nodeLevel(player, node.id) >= 1) out.push(node.capstoneSkillId);
  }
  return out;
}
